import { REGEX } from '../constants/regex';

/**
 * URL schemes that are safe to render into an `href`/`src` attribute.
 * Everything else (javascript:, vbscript:, data:, file:, ...) is rejected.
 */
const SAFE_URL_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:']);

/**
 * A relative/fragment URL (no scheme) is always allowed since it can never resolve to an
 * executable scheme like `javascript:`.
 */
const hasScheme = (url: string): boolean => REGEX.URL_SCHEME.test(url);

/**
 * Determines whether a user-supplied URL is safe to render into `href`/`src`.
 *
 * Browsers ignore whitespace (tabs, newlines, ...) embedded inside a URL scheme (e.g.
 * `java\tscript:alert(1)` is parsed as `javascript:alert(1)`), so it is stripped before the
 * scheme is inspected — otherwise that stripping-and-parsing behavior could be used to
 * smuggle a disallowed scheme past this check.
 */
export const isSafeUrl = (url: string): boolean => {
  const trimmed = url.trim();
  if (!trimmed) return false;

  const normalized = trimmed.replace(REGEX.URL_WHITESPACE, '');
  if (!hasScheme(normalized)) return true;

  const schemeMatch = normalized.match(REGEX.URL_SCHEME);
  const scheme = schemeMatch?.[1].toLowerCase();
  return scheme !== undefined && SAFE_URL_SCHEMES.has(`${scheme}:`);
};

/**
 * Returns the URL unchanged when it passes {@link isSafeUrl}, otherwise an inert fallback
 * that renders as a dead link instead of executing a disallowed scheme.
 */
export const sanitizeUrl = (url: string): string => (isSafeUrl(url) ? url : 'about:blank');

/**
 * Wraps a URL in a quoted CSS `url("...")` for use in an inline `background-image`.
 * Quotes, backslashes and line breaks are percent-encoded so a malformed value can
 * never terminate the `url()` token early, and unsafe schemes collapse to an inert URL.
 */
export const toCssUrl = (url: string): string => {
  const encoded = sanitizeUrl(url).replace(/["\\\n\r]/g, (char) => encodeURIComponent(char));
  return `url("${encoded}")`;
};
